import React from 'react';
import { Box, Typography, Container, Paper, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const TermsAndConditionsPage = () => {
  const navigate = useNavigate();

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(-1)}
        sx={{ mb: 3 }}
      >
        Back
      </Button>
      <Paper elevation={3} sx={{ p: { xs: 3, md: 5 }, borderRadius: 3 }}>
        <Typography variant="h3" gutterBottom sx={{ fontWeight: 'bold', mb: 4, textAlign: 'center' }}>
          Terms and Conditions
        </Typography>

        <Box sx={{ '& h2': { mt: 4, mb: 2, color: 'primary.main', fontWeight: 600, fontSize: '1.5rem' }, '& p, & li': { mb: 2, lineHeight: 1.7, color: 'text.secondary', fontSize: '1.05rem' }, '& ul': { pl: 3 } }}>
          <Typography variant="subtitle1" fontWeight="bold">School Management System</Typography>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>Effective Date: April 2026</Typography>

          <hr style={{ margin: '24px 0', border: 'none', borderTop: '1px solid #e0e0e0' }} />

          <h2>1. Introduction</h2>
          <p>Welcome to our School Management System (M School App-sms). These Terms and Conditions (“Terms”) govern your access to and use of the Platform by school administrators, teachers, parents, and students.</p>
          <p>By accessing or using this Platform, you agree to be bound by these Terms. If you do not agree, you must not use the Platform.</p>

          <hr style={{ margin: '24px 0', border: 'none', borderTop: '1px solid #e0e0e0' }} />

          <h2>2. Definitions</h2>
          <ul>
            <li><strong>Platform:</strong> The web and mobile application used for managing school operations.</li>
            <li><strong>User:</strong> Any individual accessing the Platform (Admin, Teacher, Parent, Student).</li>
            <li><strong>School:</strong> The institution subscribed to use the Platform.</li>
            <li><strong>Services:</strong> Features including attendance tracking, fee management, messaging, reports, and related tools.</li>
          </ul>

          <hr style={{ margin: '24px 0', border: 'none', borderTop: '1px solid #e0e0e0' }} />

          <h2>3. Eligibility</h2>
          <ul>
            <li>Users must be authorized by a registered school.</li>
            <li>Parents/guardians must act on behalf of their children.</li>
            <li>Schools are responsible for ensuring that all users under their account comply with these Terms.</li>
          </ul>

          <hr style={{ margin: '24px 0', border: 'none', borderTop: '1px solid #e0e0e0' }} />

          <h2>4. User Accounts</h2>
          <ul>
            <li>Each user must provide accurate and complete information.</li>
            <li>Login credentials must be kept confidential.</li>
            <li>Users are responsible for all activities under their accounts.</li>
            <li>The Platform reserves the right to suspend or terminate accounts for misuse.</li>
          </ul>

          <hr style={{ margin: '24px 0', border: 'none', borderTop: '1px solid #e0e0e0' }} />

          <h2>5. Acceptable Use</h2>
          <p>You agree to use the Platform only for legitimate school-related purposes.</p>
          <p>You must NOT:</p>
          <ul>
            <li>Use the Platform for unlawful or fraudulent activities</li>
            <li>Attempt to hack, disrupt, or damage the system</li>
            <li>Upload false, misleading, or harmful data</li>
            <li>Impersonate another user</li>
          </ul>

          <hr style={{ margin: '24px 0', border: 'none', borderTop: '1px solid #e0e0e0' }} />

          <h2>6. Features and Services</h2>
          <p>The Platform may include:</p>
          <ul>
            <li>Student attendance tracking</li>
            <li>Fee and payment management (including feeding fees)</li>
            <li>Academic reporting</li>
            <li>Messaging between parents, teachers, and administrators</li>
            <li>Calendar and agenda management</li>
          </ul>
          <p>Features may be updated, modified, or discontinued at any time.</p>

          <hr style={{ margin: '24px 0', border: 'none', borderTop: '1px solid #e0e0e0' }} />

          <h2>7. Payments and Fees</h2>
          <ul>
            <li>Schools may use the Platform to track and manage fees.</li>
            <li>The Platform is not responsible for disputes between schools and parents regarding payments.</li>
            <li>Any payment integrations (if applicable) are subject to third-party terms.</li>
          </ul>

          <hr style={{ margin: '24px 0', border: 'none', borderTop: '1px solid #e0e0e0' }} />

          <h2>8. Data Privacy and Protection</h2>
          <ul>
            <li>The Platform collects and processes data such as student records, attendance, and communication logs.</li>
            <li>Schools are responsible for obtaining consent from parents/guardians where required.</li>
            <li>We take reasonable steps to protect data but do not guarantee absolute security.</li>
          </ul>
          <p>By using the Platform, you consent to the collection and use of data for operational purposes.</p>

          <hr style={{ margin: '24px 0', border: 'none', borderTop: '1px solid #e0e0e0' }} />

          <h2>9. Intellectual Property</h2>
          <ul>
            <li>All content, software, and design of the Platform remain the property of the Platform provider.</li>
            <li>Users may not copy, distribute, or modify any part of the Platform without permission.</li>
          </ul>

          <hr style={{ margin: '24px 0', border: 'none', borderTop: '1px solid #e0e0e0' }} />

          <h2>10. Availability of Service</h2>
          <ul>
            <li>The Platform aims for high availability but does not guarantee uninterrupted service.</li>
            <li>Maintenance, updates, or technical issues may cause temporary downtime.</li>
          </ul>

          <hr style={{ margin: '24px 0', border: 'none', borderTop: '1px solid #e0e0e0' }} />

          <h2>11. Limitation of Liability</h2>
          <p>To the fullest extent permitted by law:</p>
          <ul>
            <li>The Platform is provided “as is” without warranties.</li>
            <li>We are not liable for:
              <ul>
                <li>Data loss</li>
                <li>Financial losses</li>
                <li>Misuse of the system by users</li>
                <li>Errors in attendance or fee records entered by users</li>
              </ul>
            </li>
          </ul>

          <hr style={{ margin: '24px 0', border: 'none', borderTop: '1px solid #e0e0e0' }} />

          <h2>12. Termination</h2>
          <p>We may suspend or terminate access if:</p>
          <ul>
            <li>These Terms are violated</li>
            <li>There is misuse or fraudulent activity</li>
            <li>The school subscription is terminated</li>
          </ul>
          <p>Users may stop using the Platform at any time.</p>

          <hr style={{ margin: '24px 0', border: 'none', borderTop: '1px solid #e0e0e0' }} />

          <h2>13. Changes to Terms</h2>
          <p>We may update these Terms from time to time. Users will be notified of significant changes, and continued use means acceptance of the updated Terms.</p>

          <hr style={{ margin: '24px 0', border: 'none', borderTop: '1px solid #e0e0e0' }} />

          <h2>14. Governing Law</h2>
          <p>These Terms shall be governed by the laws of <strong>Ghana</strong>.</p>

          <hr style={{ margin: '24px 0', border: 'none', borderTop: '1px solid #e0e0e0' }} />

          <h2>15. Contact Information</h2>
          <p>For questions or support, contact:</p>
          <ul>
            <li><strong>Phone / WhatsApp:</strong> 0594640478</li>
            <li><strong>Email:</strong> contactcorevista@gmail.com</li>
          </ul>

          <hr style={{ margin: '24px 0', border: 'none', borderTop: '1px solid #e0e0e0' }} />

          <h2>16. Acceptance</h2>
          <p>By logging in or using this Platform, you confirm that you have read, understood, and agreed to these Terms and Conditions.</p>

        </Box>
      </Paper>
      
      {/* Global Footer */}
      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
          © 2026 CoreVista Technologies. All rights reserved.
        </Typography>
      </Box>
    </Container>
  );
};

export default TermsAndConditionsPage;
